export const selfServiceEn = {
    uploadDocument: 'Upload document',
    chooseDocumentFile: 'Choose file (required)',
    documentName: 'Name (required)',
    uploadingDocument: 'Uploading file…',
    openDocument: 'Open document',
    requestUpdated: 'Request updated',
    requestSubmitted: 'Request submitted',
    requestCancelled: 'Request cancelled',
    cancelRequest: 'Cancel request',
    pendingValue: 'Requested: {{value}}',
    currentlyUsed: 'Currently used',
    waitingForApproval: 'Waiting for approval',
    pendingHint: 'Edit and save to replace your pending request.',
    alwaysVisibleHint: 'View cannot be turned off for basic personal and contact details because members can always view these fields in the Community-App, even when self-service is disabled. Editing and review can still be configured for editable fields.',
    defaultReviewers: 'Default reviewers',
    reviewerRules: 'Reviewer rules',
    reviewerRulesHint: 'Reviewers from all matching rules are combined. Conditions use the current stored contact details. Reviewers still need permission to review each changed field.',
    reviewerRule: 'Rule {{number}}',
    reviewerContactType: 'Contact type',
    reviewerContactTypeHint: 'Choose a contact type to use its properties and custom fields. Changing the type clears the condition.',
    allContactTypes: 'All contact types',
    reviewerCondition: 'Contact condition',
    addReviewerRule: 'Add reviewer rule',
    removeReviewerRule: 'Remove rule',
    invalidReviewerRule: 'This rule references a missing or incompatible field and is ignored. Edit its condition or remove the rule.',
    assignment: 'Assignment',
    assignedToMe: 'Assigned to me',
    allAssignments: 'All changes',
    clearContactFilter: 'Show all contacts',
    contactFilter: 'Contact',
    yes: 'Yes',
    no: 'No',
    title: 'Self-service',
    description: 'Let members view and update their details in the Community-App. Choose which properties they can access and whether changes apply immediately or require review. All changes are recorded in history.',
    settings: 'Settings',
    propertiesTitle: 'Properties',
    changes: 'Changes',
    myChanges: 'My changes',
    enabled: 'Enable self-service',
    accessLimits: 'Restrict self-service access',
    accessLimitsHint: 'Select who can use self-service for this membership. Leave all fields empty to allow everyone. A contact must match every configured category and at least one value within each category. Other contacts keep the basic read-only membership view.',
    reviewers: 'Reviewers',
    ownerFallback: 'Default reviewers receive changes when no matching rule has a reviewer with the required permissions. If no default reviewer is eligible, organization owners receive the changes.',
    search: 'Search properties',
    property: 'Property',
    access: 'Member access',
    selected: '{{count}} selected',
    modes: { read: 'View', immediate: 'Edit', review: 'Requires review' },
    modeHints: {
        read: 'Members can view this property in self-service. Turning off View also turns off Edit and Requires review, hides the property, and counts it as unused.',
        immediate: 'Members can edit this property. Selecting Edit also selects View. Turning off Edit also turns off Requires review. Without review, changes are applied directly. Available only for editable properties.',
        review: 'Changes require approval before they are applied. Selecting Requires review also selects View and Edit. Turning it off keeps View and Edit selected. Available only for editable properties.',
    },
    groups: { personal: 'Personal details', contact: 'Contact details', payment: 'Payment details', membership: 'Membership', contracts: 'Contracts', documents: 'Documents' },
    propertyDescriptions: {
        alternativeContacts: 'View shows existing alternative contacts. Edit lets members add new entries, update existing entries and remove entries. For linked contacts, changes to names and communication details also update the linked contact record. Removing an entry only removes it from the alternative contacts list; it does not delete the linked contact. With Requires review, additions, changes and removals wait for approval.',
        contracts: 'View shows the contact’s contracts by name in Contracts & fees, when contracts exist. Enable the individual contract properties below to show amounts, billing dates, status and other details. If View is off for Contracts, all contracts and their details stay hidden, even when individual properties are enabled. Contracts are read-only.',
        paymentMethod: 'View shows the current payment method. Edit lets members choose another available method. With Requires review, the change waits for approval. Switching to or from direct debit also requires Edit for Direct-debit details.',
        directDebitDetails: 'View shows the account holder and bank details. Edit lets members change the account holder, IBAN and required BIC together, with direct-debit authorization. If a payment-method change also affects these details, the entire change waits for approval when either property requires review.',
        billingDetails: 'View shows the invoice recipient, billing address, email and delivery method. Edit lets members update these together; Requires review makes changes subject to approval. Changes apply to the shared billing account and its memberships. Personal contact details stay separate.',
        debitAuthorization: 'Shows the reference and signature date of the existing SEPA mandate when direct debit is active. This information is read-only. Members give direct-debit authorization when submitting bank details.',
        paymentOverview: 'Shows outstanding invoices, outstanding credits and credit on account, with a link to receipts when available. These amounts are calculated from the billing account and are read-only.',
        onlinePaymentMethods: 'View shows saved online payment methods and their status. Edit lets members set up or replace them through the payment provider. These changes apply immediately and cannot require review. Changing the membership payment method is controlled separately.',
        documents: 'View lets members see and download documents shared with their contact. Edit also lets them upload new documents. With Requires review, uploads are added only after approval. Members cannot edit or delete existing documents.',
    },
    deliveryMethods: { none: 'No automatic delivery', email: 'Email', postal: 'Post' },
    contractFields: {
        amount: 'Next collection amount',
        dueAt: 'Next billing date',
        interval: 'Billing interval',
        status: 'Status',
        startAt: 'Start date',
        endAt: 'End date',
        terminatedAt: 'Termination recorded on',
    },
    properties: {
        documents: 'Documents shared with the contact',
        person: { firstName: 'First name', lastName: 'Last name', birthdate: 'Birthdate', gender: 'Gender' },
        address: 'Address',
        communication: { email: 'Email', phone: 'Phone', mobilePhone: 'Mobile' },
        alternativeContacts: 'Alternative contacts',
        paymentMethod: 'Payment method',
        directDebitDetails: 'Direct-debit details',
        billingDetails: 'Billing details',
        debitAuthorization: 'Direct debit authorization',
        paymentOverview: 'Payment overview',
        onlinePaymentMethods: 'Saved online payment methods',
        departments: 'Departments',
        contracts: 'Contracts',
    },
    fields: {
        billingAddress: 'Billing address',
        receiptSendMethod: 'Invoice delivery',
        paymentMethod: 'Payment method',
        entryAt: 'Entry',
        exitAt: 'Exit',
        name: 'Name',
        description: 'Description',
        email: 'Email',
        phone: 'Phone',
        mobilePhone: 'Mobile',
        firstName: 'First name',
        lastName: 'Last name',
        iban: 'IBAN',
        bic: 'BIC',
        accountHolderName: 'Account holder',
        country: 'Country',
        state: 'State / region',
        zip: 'Postal code',
        city: 'City',
        addressLine: 'Street and number',
        details1: 'Address supplement',
        details2: 'Additional address details',
    },
    current: 'Current',
    proposed: 'Requested',
    submitted: 'Submitted',
    reviewRequired: 'This change requires approval.',
    conflict: 'Changed since submission',
    rejectHint: 'Reject this request? The stored information will remain unchanged.',
    conflictHint: 'Review the current value before accepting this change.',
    accept: 'Accept',
    reject: 'Reject',
    withdraw: 'Withdraw',
    correct: 'Correct',
    add: 'Add',
    remove: 'Remove',
    cancel: 'Cancel',
    edit: 'Edit',
    saved: 'Changes saved',
    savedResult: '{{applied}} applied · {{pending}} pending',
    reviewed: 'Review saved',
    empty: 'No changes',
    emptyHint: 'Submitted changes will appear here.',
    noProperties: 'No matching properties',
    noPropertiesHint: 'Try another search.',
    noValue: 'Not provided',
    removed: 'Remove entry',
    all: 'All',
    pending: 'Pending',
    debitAuthorization: 'I authorize direct debits from this account.',
    errors: {

        disabled: 'Self-service editing is not available.',
        forbidden: 'You cannot change this property.',
        changed: 'This information changed. Refresh and review the current value.',
        invalidPayment: 'Choose an available payment method and provide its required details.',
        bankUnavailable: 'Direct debit is unavailable for this account.',
        reviewer: 'You do not have access to review these changes.',
        missing: 'This change is no longer pending.',
        authorization: 'Confirm the direct-debit authorization.',
        unavailable: 'This property is no longer available.',
    },
    notifications: {
        reviewTitle: 'Contact changes to review',
        reviewMessage: '{{name}} submitted changes.',
        outcomeTitle: 'Your contact changes',
        outcomeMessage: 'Your organization reviewed your changes.',
        open: 'View changes',
    },
}
